import React, { useCallback, useEffect, useState } from 'react';
import { Send, Copy, CheckCircle2, Archive, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { unwrap, errMsg, btnPrimary, btnGhost, textareaClass, inputClass, CAPABILITY_BADGE } from './shared';

const BADGE = { draft: 'sf-badge-neutral', in_review: 'sf-badge-warning', approved: 'sf-badge-success', ready_to_publish: 'sf-badge-manager', published: 'sf-badge-success', failed: 'sf-badge-error', archived: 'sf-badge-neutral' };
const STATUS_FILTERS = ['', 'draft', 'in_review', 'approved', 'ready_to_publish', 'published', 'failed', 'archived'];

export default function ContentStudio() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(null);
  const [caps, setCaps] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setItems(unwrap(await api.get('/linkedin-ai/drafts', { params: status ? { status } : {} })).items || []); } catch (e) { alertService.error('Could not load drafts', errMsg(e)); setItems([]); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/linkedin-ai/capabilities').then((r) => setCaps(unwrap(r))).catch(() => setCaps(null)); }, []);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg) alertService.success(okMsg); await load(); return r; } catch (e) { alertService.error('Action failed', errMsg(e)); } finally { setBusy(false); }
  };
  const openDraft = async (id) => { try { setOpen(unwrap(await api.get(`/linkedin-ai/drafts/${id}`)).draft); } catch (e) { alertService.error('Could not open draft', errMsg(e)); } };
  const refreshOpen = (d) => setOpen(d);

  const move = (d, to, extra = {}) => act(async () => { const r = unwrap(await api.post(`/linkedin-ai/drafts/${d._id}/transition`, { to, ...extra })); refreshOpen(r.draft); });
  const save = (d) => act(async () => { refreshOpen(unwrap(await api.patch(`/linkedin-ai/drafts/${d._id}`, { title: d.title, body: d.body })).draft); }, 'Saved. Any earlier approval was cleared.');

  const publishNow = async (d) => {
    const t = d.publishTarget || {};
    const ok = await alertService.confirm('Publish to LinkedIn now?', `This posts the approved text below as a real LinkedIn post using the connected account. It cannot be undone from here.\n\n"${d.body.slice(0, 280)}${d.body.length > 280 ? '…' : ''}"\n\nTarget: ${t.authorUrn || 'your personal profile'}`, 'warning', 'Publish now');
    if (!(ok?.isConfirmed ?? ok)) return;
    act(async () => {
      try { const r = unwrap(await api.post(`/linkedin-ai/drafts/${d._id}/publish`, { confirm: true })); refreshOpen(r.draft); alertService.success('Published'); } catch (e) { alertService.error('Publishing failed', errMsg(e)); await openDraft(d._id); }
    });
  };

  const scheduleIt = async (d, when) => {
    const ok = await alertService.confirm('Schedule this post?', `It will be posted to LinkedIn at ${when.toLocaleString()} using the connected account, if the approval and connection are still valid then.\n\n"${d.body.slice(0, 200)}${d.body.length > 200 ? '…' : ''}"`, 'question', 'Schedule');
    if (!(ok?.isConfirmed ?? ok)) return;
    act(async () => { refreshOpen(unwrap(await api.post(`/linkedin-ai/drafts/${d._id}/schedule`, { confirm: true, scheduledFor: when.toISOString() })).draft); }, 'Scheduled');
  };

  const accounts = (caps?.accounts || []).filter((a) => a.status === 'active');
  const pub = caps?.capabilities?.find((c) => c.key === 'member_publish');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
      <aside className="space-y-3">
        <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, ' ') : 'All statuses'}</option>)}
        </select>
        {items === null ? <p className="text-[13px] text-[#5C5C5C]">Loading…</p> : items.length === 0 ? (
          <div className="sf-card p-6 text-center text-[13px] text-[#5C5C5C]">No drafts yet. Run a skill and choose “Save to Content Studio”.</div>
        ) : (
          <ul className="space-y-2">
            {items.map((d) => (
              <li key={d._id}>
                <button onClick={() => openDraft(d._id)} className={`w-full text-left sf-card p-3 hover:border-[#0176D3] ${open?._id === d._id ? 'border-[#0176D3]' : ''}`}>
                  <div className="flex justify-between gap-2"><p className="text-[13px] font-bold truncate">{d.title || '(untitled)'}</p><span className={`sf-badge ${BADGE[d.status]}`}>{d.status.replace(/_/g, ' ')}</span></div>
                  <p className="text-[12px] text-[#5C5C5C] line-clamp-2 mt-1">{d.body}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="min-w-0">
        {!open ? <div className="sf-card p-10 text-center text-[13px] text-[#5C5C5C]">Select a draft to review, approve and publish.</div> : (
          <div className="sf-card p-4 space-y-3">
            <input className={inputClass} value={open.title} onChange={(e) => setOpen({ ...open, title: e.target.value })} aria-label="Title" disabled={['published', 'archived'].includes(open.status)} />
            <textarea className={`${textareaClass} min-h-[220px]`} value={open.body} maxLength={3000} onChange={(e) => setOpen({ ...open, body: e.target.value })} aria-label="Post text" disabled={['published', 'archived'].includes(open.status)} />
            <p className="text-[11px] text-[#706E6B]">{[...open.body].length}/3000 · status <span className={`sf-badge ${BADGE[open.status]}`}>{open.status.replace(/_/g, ' ')}</span></p>

            {open.reviewWarnings?.length > 0 && (
              <ul className="text-[12px] space-y-1">
                {open.reviewWarnings.map((w, i) => <li key={i} className={w.severity === 'error' ? 'text-[#BA0517]' : w.severity === 'info' ? 'text-[#5C5C5C]' : 'text-[#7A5A00]'}>• {w.message}</li>)}
              </ul>
            )}
            {open.publishError && <p role="alert" className="text-[12px] text-[#BA0517] bg-[#FDE7E9] border border-[#F6A3AB] rounded px-3 py-2">{open.publishError}</p>}

            <div className="flex flex-wrap gap-2 pt-1">
              {!['published', 'archived'].includes(open.status) && <button className={btnGhost} disabled={busy} onClick={() => save(open)}>Save changes</button>}
              <button className={btnGhost} onClick={() => navigator.clipboard?.writeText(open.body)}><Copy size={13} className="inline mr-1" />Copy text</button>
              {open.status === 'draft' && <button className={btnPrimary} disabled={busy} onClick={() => move(open, 'in_review')}>Submit for review</button>}
              {open.status === 'in_review' && <><button className={btnPrimary} disabled={busy} onClick={() => move(open, 'approved')}><CheckCircle2 size={13} className="inline mr-1" />Approve this text</button><button className={btnGhost} disabled={busy} onClick={() => move(open, 'draft')}>Back to draft</button></>}
              {['approved', 'failed'].includes(open.status) && open.approvedBodyHash && (
                accounts.length && pub?.status === 'available' ? (
                  <ReadyForm accounts={accounts} busy={busy} onReady={(accountId, authorUrn) => move(open, 'ready_to_publish', { target: { accountId, authorUrn } })} />
                ) : <p className="text-[12px] text-[#5C5C5C] basis-full">Direct publishing is not available for your account ({pub?.reason || 'no LinkedIn account connected'}). Copy the text and post it on LinkedIn yourself, then archive the draft. <a className="text-[#0176D3] inline-flex items-center gap-1" href="https://www.linkedin.com/feed/" target="_blank" rel="noreferrer noopener">Open LinkedIn <ExternalLink size={11} /></a></p>
              )}
              {open.status === 'ready_to_publish' && open.scheduledFor && (
                <div className="basis-full flex flex-wrap items-center gap-2 text-[12px]">
                  <span className="sf-badge sf-badge-manager">Scheduled for {new Date(open.scheduledFor).toLocaleString()}</span>
                  <span className="text-[#5C5C5C]">Approval and your connection are re-checked when it fires; if anything changed it will not post.</span>
                  <button className={btnGhost} disabled={busy} onClick={() => act(async () => refreshOpen(unwrap(await api.delete(`/linkedin-ai/drafts/${open._id}/schedule`)).draft), 'Schedule cancelled')}>Cancel schedule</button>
                </div>
              )}
              {open.status === 'ready_to_publish' && !open.scheduledFor && (
                <>
                  <button className={btnPrimary} disabled={busy} onClick={() => publishNow(open)}><Send size={13} className="inline mr-1" />Publish to LinkedIn…</button>
                  <ScheduleForm busy={busy} onSchedule={(when) => scheduleIt(open, when)} />
                </>
              )}
              {!['archived', 'published'].includes(open.status) && <button className={btnGhost} disabled={busy} onClick={() => move(open, 'archived')}><Archive size={13} className="inline mr-1" />Archive</button>}
            </div>
            {open.history?.length > 0 && (
              <details className="text-[12px]"><summary className="cursor-pointer font-semibold text-[#0176D3]">History</summary>
                <ul className="mt-2 space-y-1 text-[#5C5C5C]">{[...open.history].reverse().map((h, i) => <li key={i}>{new Date(h.at).toLocaleString()} · {h.from || '—'} → {h.to}{h.note ? ` · ${h.note}` : ''}</li>)}</ul>
              </details>
            )}
          </div>
        )}
        {caps && !caps.encryptionConfigured && <p className="mt-3 text-[12px] text-[#BA0517]">Server setting FIELD_ENCRYPTION_KEY is missing, so LinkedIn tokens are not encrypted at rest. Ask your administrator to set it.</p>}
        {pub && <p className="mt-3 text-[11px] text-[#706E6B]">Publishing status: <span className={`sf-badge ${CAPABILITY_BADGE[pub.status]?.[0]}`}>{CAPABILITY_BADGE[pub.status]?.[1]}</span></p>}
      </section>
    </div>
  );
}

function ReadyForm({ accounts, busy, onReady }) {
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const acc = accounts.find((a) => a.id === accountId);
  const [author, setAuthor] = useState('');
  return (
    <div className="basis-full flex flex-wrap items-center gap-2">
      <select className={`${inputClass} !w-auto`} value={accountId} onChange={(e) => { setAccountId(e.target.value); setAuthor(''); }} aria-label="LinkedIn account">
        {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || 'LinkedIn account'}</option>)}
      </select>
      <select className={`${inputClass} !w-auto`} value={author} onChange={(e) => setAuthor(e.target.value)} aria-label="Post as">
        <option value="">Personal profile</option>
        {(acc?.organizations || []).map((o) => <option key={o.urn} value={o.urn}>{o.name || o.urn}</option>)}
      </select>
      <button className={btnPrimary} disabled={busy} onClick={() => onReady(accountId, author || undefined)}>Mark ready to publish</button>
    </div>
  );
}

function ScheduleForm({ busy, onSchedule }) {
  const [at, setAt] = useState('');
  return (
    <div className="flex items-center gap-2">
      <input type="datetime-local" className={`${inputClass} !w-auto`} value={at} onChange={(e) => setAt(e.target.value)} aria-label="Schedule for" />
      <button className={btnGhost} disabled={busy || !at} onClick={() => onSchedule(new Date(at))}>Schedule…</button>
    </div>
  );
}
